import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Paper,
  Box,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  Chip,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import NavigationHeader from "components/NavigationHeader";
import { TitleTable } from "components/TitleTable";
import { DashboardStackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import BackdropLoading from "components/BackdropLoading";
import { apiAntigo } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import { debounce } from "lodash";

interface DualListItem {
  id: number;
  label: string;
  subtitle?: string;
}

export const AdicionarMateriaisDiversa = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nivel = searchParams.get("nivel") || "n1";
  const { openNotification } = useNavigation();

  const [isLoading, setIsLoading] = useState(false);
  const [liberacaoInfo, setLiberacaoInfo] = useState<any>(null);

  // Busca
  const [busca, setBusca] = useState("");

  // Estados para N1 (Produtos)
  const [linhaId, setLinhaId] = useState<number | "">("");
  const [linhasOpcoes, setLinhasOpcoes] = useState<any[]>([]);
  const [materiaisDisponiveis, setMateriaisDisponiveis] = useState<DualListItem[]>([]);
  const [materiaisSelecionados, setMateriaisSelecionados] = useState<DualListItem[]>([]);

  // Estados para N2 (Tipologias)
  const [projetistaId, setProjetistaId] = useState<number | "">("");
  const [linhaIdN2, setLinhaIdN2] = useState<number | "">("");
  const [projetistasOpcoes, setProjetistasOpcoes] = useState<any[]>([]);
  const [linhasN2Opcoes, setLinhasN2Opcoes] = useState<any[]>([]);

  // Auto-save debounced
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (id) {
      carregarDadosLiberacao();
      carregarOpcoesIniciais();
    }
  }, [id]);

  useEffect(() => {
    if (nivel === "n1" && linhaId) {
      carregarMateriaisDisponiveis();
    }
  }, [nivel, linhaId]);

  useEffect(() => {
    if (nivel === "n2") {
      carregarTipologiasDisponiveis();
    }
  }, [nivel, projetistaId, linhaIdN2]);

  const carregarDadosLiberacao = async () => {
    try {
      const { data } = await apiAntigo.get(`/LiberacaoDiversa/Liberacao/${id}`);
      setLiberacaoInfo(data);
    } catch (error: any) {
      console.error("Erro ao carregar dados da liberação:", error);
    }
  };

  const carregarOpcoesIniciais = async () => {
    try {
      if (nivel === "n1") {
        const { data: linhas } = await apiAntigo.get("/geral/selecaoLinha");
        setLinhasOpcoes(linhas || []);
      } else {
        const [projetistasResp, linhasResp] = await Promise.all([
          apiAntigo.get("/geral/selecaoProjetista"),
          apiAntigo.get("/geral/selecaoLinha"),
        ]);
        setProjetistasOpcoes(projetistasResp.data || []);
        setLinhasN2Opcoes(linhasResp.data || []);
      }
    } catch (error: any) {
      console.error("Erro ao carregar opções:", error);
    }
  };

  const carregarMateriaisDisponiveis = async () => {
    if (!id || !linhaId) return;
    
    setIsLoading(true);
    try {
      // Buscar lista completa de pacotes
      // Sistemas 1-5 (CEM / ONE / PAR) e 8 (CEM MOBI) usam a mesma lógica de CEM.
      const sistemaValor = liberacaoInfo?.sistema?.value as number | undefined;
      const isCEM = sistemaValor !== undefined && (sistemaValor <= 5 || sistemaValor === 8);
      const endpointLista = isCEM ? "/geral/SelecaoPacote" : "/geral/SelecaoPacotePrefsuite";
      const { data: todosPacotes } = await apiAntigo.get(endpointLista);

      const disponiveis: DualListItem[] = (todosPacotes || []).map((p: any) => ({
        id: p.value || p.Value,
        label: p.label || p.Label || "",
      }));

      // Buscar pacotes já selecionados
      const endpointSelecionados = isCEM
        ? `/LiberacaoDiversa/LiberacaoPortfolioDisponivel/${id}/${linhaId}`
        : `/LiberacaoDiversa/LiberacaoPortfolioPrefSuiteDisponivel/${id}/${linhaId}`;
        
      const { data: selectedData } = await apiAntigo.get(endpointSelecionados);
      
      const idsSelected = selectedData.selectedOption || selectedData || [];
      const selecionados: DualListItem[] = disponiveis.filter((d) => idsSelected.includes(d.id));

      setMateriaisDisponiveis(disponiveis);
      setMateriaisSelecionados(selecionados);
    } catch (error: any) {
      console.error("Erro ao carregar materiais disponíveis:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const carregarTipologiasDisponiveis = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      // Buscar lista completa de tipologias
      const params: any = {};
      if (projetistaId) params.projetistaId = projetistaId;
      if (linhaIdN2) params.linhaId = linhaIdN2;

      const { data: todasTipologias } = await apiAntigo.get("/LiberacaoPadraoCEM/ListarTipologias", { params });

      const disponiveis: DualListItem[] = (todasTipologias || []).map((t: any) => ({
        id: t.value || t.Value,
        label: t.label || t.Label || "",
      }));

      // Buscar tipologias já selecionadas
      const { data: selectedData } = await apiAntigo.get(`/LiberacaoDiversa/LiberacaoTipologiaDisponivel/${id}`);
      
      const idsSelected = selectedData.selectedTipologias || [];
      const selecionados: DualListItem[] = disponiveis.filter((d) => idsSelected.includes(d.id));

      setMateriaisDisponiveis(disponiveis);
      setMateriaisSelecionados(selecionados);
    } catch (error: any) {
      console.error("Erro ao carregar tipologias disponíveis:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSalvarMateriaisAuto = useCallback((selection: DualListItem[]) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      await salvarMateriais(selection);
    }, 800);
  }, [id, nivel, linhaId, projetistaId, linhaIdN2]);

  const salvarMateriais = async (selection: DualListItem[]) => {
    if (!id) return;

    try {
      if (nivel === "n1") {
        const sistemaValor = liberacaoInfo?.sistema?.value as number | undefined;
        const isCEM = sistemaValor !== undefined && (sistemaValor <= 5 || sistemaValor === 8);
        const payload = {
          idLiberacaoDiversa: Number(id),
          idLinha: linhaId,
          idsAtualizacaoPortfolio: selection.map((p) => p.id),
        };

        if (isCEM) {
          await apiAntigo.post("/LiberacaoDiversa/LiberacaoPortfolioCEM", payload);
        } else {
          await apiAntigo.post("/LiberacaoDiversa/LiberacaoPortfolioPrefSuite", payload);
        }
      } else {
        const payload = {
          idLiberacaoDiversa: Number(id),
          idsTipologia: selection.map((t) => t.id),
          idProjetista: projetistaId || null,
          idLinha: linhaIdN2 || null,
        };

        await apiAntigo.post("/LiberacaoDiversa/LiberacaoTipologia", payload);
      }
    } catch (error: any) {
      console.error("Erro ao salvar materiais:", error);
    }
  };

  const handleSalvar = async () => {
    setIsLoading(true);
    try {
      await salvarMateriais(materiaisSelecionados);
      
      openNotification({
        message: "Materiais salvos com sucesso!",
        type: "success",
      });
      
      navigate(`/liberacoesDiversas/${id}/materiais`);
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filtrarLista = (lista: DualListItem[], termo: string) => {
    if (!termo.trim()) return lista;
    const termoLower = termo.toLowerCase();
    return lista.filter((item) =>
      item.label.toLowerCase().includes(termoLower) ||
      item.subtitle?.toLowerCase().includes(termoLower)
    );
  };

  return (
    <DashboardStackCustom>
      <NavigationHeader
        title="Adicionar materiais para CEM PRO"
        label={`Selecione os materiais disponíveis para liberação (${nivel === "n1" ? "Cadastro N1" : "Cadastro N2"})`}
        hasBackButton={true}
        goBack={() => navigate(`/liberacoesDiversas/${id}/materiais`)}
      />

      <Paper elevation={3}>
        <Box sx={{ p: 3 }}>
          {/* Seção de filtros */}
          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 2,
              mb: 3,
              bgcolor: "background.paper",
            }}
          >
            <Chip label="MERCADO" color="warning" size="small" sx={{ mb: 2 }} />

            {nivel === "n1" ? (
              <TextField
                select
                fullWidth
                label="Linha"
                value={linhaId}
                onChange={(e) => setLinhaId(e.target.value as number | "")}
                size="small"
              >
                <MenuItem value="">Selecione uma linha...</MenuItem>
                {linhasOpcoes.map((linha: any) => (
                  <MenuItem key={linha.value || linha.Value} value={linha.value || linha.Value}>
                    {linha.label || linha.Label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Projetista"
                    value={projetistaId}
                    onChange={(e) => setProjetistaId(e.target.value as number | "")}
                    size="small"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {projetistasOpcoes.map((p: any) => (
                      <MenuItem key={p.value || p.Value} value={p.value || p.Value}>
                        {p.label || p.Label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Linha"
                    value={linhaIdN2}
                    onChange={(e) => setLinhaIdN2(e.target.value as number | "")}
                    size="small"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {linhasN2Opcoes.map((l: any) => (
                      <MenuItem key={l.value || l.Value} value={l.value || l.Value}>
                        {l.label || l.Label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Lista de materiais */}
          <TitleTable
            title={nivel === "n1" ? "Tipo + Referências + Linha" : "Projetista + Linha/Sistema"}
          />

          <TextField
            fullWidth
            placeholder="Busque a referência ou linha"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            size="small"
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              maxHeight: 500,
              overflow: "auto",
            }}
          >
            {filtrarLista(materiaisDisponiveis, busca).map((item) => {
              const isChecked = materiaisSelecionados.some((m) => m.id === item.id);
              return (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: 1,
                    borderColor: "divider",
                    "&:last-child": { borderBottom: 0 },
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <FormControlLabel
                    sx={{ width: "100%", m: 0, py: 1, px: 2 }}
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) => {
                          let newSelection: DualListItem[];
                          if (e.target.checked) {
                            newSelection = [...materiaisSelecionados, item];
                          } else {
                            newSelection = materiaisSelecionados.filter(
                              (m) => m.id !== item.id
                            );
                          }
                          setMateriaisSelecionados(newSelection);
                          handleSalvarMateriaisAuto(newSelection);
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{item.label}</Typography>
                        {item.subtitle && (
                          <Typography variant="caption" color="text.secondary">
                            {item.subtitle}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </Box>
              );
            })}

            {filtrarLista(materiaisDisponiveis, busca).length === 0 && (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  {materiaisDisponiveis.length === 0
                    ? "Selecione os filtros acima para carregar os materiais disponíveis"
                    : "Nenhum item encontrado com esse termo de busca"}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Botões de ação */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/liberacoesDiversas/${id}/materiais`)}
            >
              CANCELAR
            </Button>
            <Button variant="contained" onClick={handleSalvar} disabled={isLoading}>
              SALVAR
            </Button>
          </Box>
        </Box>
      </Paper>

      <BackdropLoading isOpen={isLoading} />
    </DashboardStackCustom>
  );
};

